<script setup lang="ts">
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useAuthStore } from '@/store/auth-store';
import { computed, onMounted, shallowRef } from 'vue';
import { store as vuex } from '@/store/store';
import { useRules } from '@/composables/vuetify-validation';

const authStore = useAuthStore();
const snackbarManager = useSnackbarManager();

const props = withDefaults(
  defineProps<{
    loginEmail?: string;
    secretCode?: string;
  }>(),
  { loginEmail: 'your inbox', secretCode: '' }
);

const clientConfig = computed(() => vuex.state.clientConfig!);

const resetCode = shallowRef(props.secretCode);
const emailAddress = shallowRef(props.loginEmail);
const serviceErrors = shallowRef('');

function resetMessage(message: string, actionText: string): void {
  snackbarManager.show({ message: message, actionText: actionText });
}

onMounted(async () => {
  if (resetCode.value) {
    await onSubmit();
  }
});

const { rules, validateAndSubmit } = useRules<{ resetCode: string }>({
  resetCode: {
    rules: [
      (v: string) => !!v || `${i18n.t('resetCode')} ${i18n.t('formfield.IsRequired')}`,
      (v: string) => !v || !!v.trim() || `${i18n.t('resetCode')} ${i18n.t('formfield.NotEmpty')}`,
    ],
  },
});

const onSubmit = () =>
  validateAndSubmit(async function (): Promise<void> {
    serviceErrors.value = '';

    const result = await authStore.confirmIdentity(props.loginEmail, resetCode.value);
    if (!result.success) {
      const error = i18n.tc(result.error);
      serviceErrors.value = error;
      resetMessage(error, i18n.tc('loginRetry') as string);
    }
  });
</script>

<template>
  <VDialog
    content-class="au-popup-dialog"
    max-width="600"
    origin="center center"
    overlay-color="secondary"
    overlay-opacity="0.25"
    persistent
    transition="scale-transition"
    :value="true"
  >
    <v-form novalidate @submit.prevent>
      <v-card v-if="clientConfig">
        <VCardTitle>
          <span class="headline">{{
            $t('loginConfirmIdentityTitle', { title: clientConfig.systemTitleShort })
          }}</span>
        </VCardTitle>
        <VCardText>
          <VContainer class="mt-6">
            <VAlert v-if="serviceErrors" type="error">
              {{ serviceErrors }}
              <br />
              {{ $t('loginConfirmIdentityError2') }}
            </VAlert>
            <VAlert v-else border="left" color="warning" icon="mdi-information" text>
              {{ $t('loginConfirmIdentityInfo1', { title: clientConfig.systemTitleShort }) }}
              <br />
              {{ $t('loginConfirmIdentityInfo2', { emailAddress: emailAddress }) }}
            </VAlert>
          </VContainer>

          <VContainer class="mt-6">
            <VTextField
              v-model="resetCode"
              autofocus
              v-bind="rules.resetCode"
              :label="$t('resetCode')"
              required
            >
            </VTextField>
          </VContainer>
        </VCardText>

        <VCardActions>
          <VBtn class="d-none d-sm-flex" color="secondary" text to="/">
            {{ $t('cancelButton') }}
          </VBtn>
          <VSpacer />
          <VBtn class="px-8 mx-4" color="primary" type="submit" @click="onSubmit">
            <VIcon left>mdi-account-key</VIcon>
            {{ $t('loginConfirmIdentityButton') }}
          </VBtn>
        </VCardActions>
      </v-card>
    </v-form>
  </VDialog>
</template>

<style lang="scss" scoped></style>
